import React, {useEffect, useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {toast} from 'react-toastify';

import {fetchStaffList} from 'api';
import {ReactComponent as MinusIcon} from 'assets/svg/minus.svg';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {
    loadParticipantsDataFx,
    onChangeParticipantIdForRemoveConfirmation,
    onSetEditParticipant,
} from 'models/profile/common';
import {
    boundUserWithParticipantFx,
    onChangeIdsForBoundConfirmation,
} from 'models/profile/staff/table';
import {UserPermission} from 'types';
import {Participant} from 'types/participants';
import {StaffAccessors as Accessors, StaffListFormattedRow, StaffListRawRow} from 'types/profile';
import {usePermission} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';

import {Field, FormFields} from '../styled';

import {ParticipantTabs} from './NavigationTabs';
import {Action, ActionsContainer, AddFooter, ButtonContainer, ButtonRemove} from './styled';

function convertData(data: StaffListRawRow[] | undefined, t: TFunction): StaffListFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [Accessors.id]: handleNil(elem.id),
        [Accessors.name]: handleNil(`${elem.firstName} ${elem.middleName || ''} ${elem.lastName}`),
        [Accessors.email]: handleNil(elem.email),
        [Accessors.phone]: handleNil(elem.phone),
        [Accessors.actions]: handleNil(elem, (value: StaffListRawRow) => (
            <ActionsContainer>
                <Action
                    onClick={() =>
                        onChangeIdsForBoundConfirmation({clientId: null, userId: value.id})
                    }
                >
                    <Icon source={MinusIcon} fillColor="#F54848" width={18} height={18} />
                </Action>
            </ActionsContainer>
        )),
    }));
}

type Props = {participant: Participant};

export const ParticipantItem = ({participant}: Props) => {
    const [index, setIndex] = useState(0);
    const {t} = useTranslation();
    const history = useHistory();
    const profile = useStore(profile$);

    const hasPermission = usePermission([UserPermission.PROFILE_DATA_EDITING]);

    const getConfig = (t: TFunction) => [
        {
            id: Accessors.name,
            header: t('pages.profile.staff.name'),
        },
        {
            id: Accessors.email,
            header: t('pages.profile.staff.email'),
        },
        {
            id: Accessors.phone,
            header: t('pages.profile.staff.phone'),
            width: 300,
        },
        ...(hasPermission
            ? [
                  {
                      id: Accessors.actions,
                      header: t('pages.profile.staff.actions'),
                      width: 60,
                  },
              ]
            : []),
    ];

    const storageFields = useMemo<Partial<Record<keyof Participant, string>>>(
        () => ({
            storageType: participant.storageType
                ? t(`dictionaries.storageType.${participant.storageType.id}.label`)
                : '—',
            storageName: participant.storageName,
            storageGln: participant.storageGln,
            address: participant.address?.address || '—',
        }),
        [t],
    );

    const StorageTab = () => {
        const onEdit = () => {
            onSetEditParticipant(participant);
            history.push(ROUTE.PROFILE_CREATE);
        };
        return (
            <>
                <FormFields>
                    {Object.entries(storageFields).map(([key, value]) => (
                        <Field key={key}>
                            <span>{t(`pages.profile.participant.fields.${key}`)}</span>
                            <Input value={value ?? undefined} readOnly />
                        </Field>
                    ))}
                </FormFields>
                <ButtonContainer>
                    <PermissionAccess permissions={[UserPermission.PROFILE_DATA_EDITING]}>
                        {participant.id !== profile?.client?.id && (
                            <ButtonRemove
                                onClick={() =>
                                    onChangeParticipantIdForRemoveConfirmation(
                                        participant.id || null,
                                    )
                                }
                            >
                                {t('pages.profile.participant.buttons.remove')}
                            </ButtonRemove>
                        )}
                        <Button onClick={onEdit}>
                            {t('pages.profile.participant.buttons.edit')}
                        </Button>
                    </PermissionAccess>
                </ButtonContainer>
            </>
        );
    };

    const PersonalTab = () => {
        const columns = useMemo(() => mapTableColumns(getConfig(t)), [t]);
        const mappedData = React.useMemo(() => convertData(participant.linkedUsers, t), [
            participant.linkedUsers,
            t,
        ]);

        const [addUsersShow, setAddUsersShow] = useState(false);
        const [selectedUser, setSelectedUser] = useState<SelectOption<number | null> | null>(null);
        const [users, setUsers] = useState<StaffListRawRow[]>([]);
        const getUsers = async () => {
            try {
                const response = await fetchStaffList();
                setUsers(response);
            } catch (error) {
                console.error(error);
            }
        };
        useEffect(() => {
            if (addUsersShow) getUsers();
        }, [addUsersShow]);
        const userOptions = useMemo<SelectOption<number | null>[]>(() => {
            try {
                return users
                    .filter((item) => !item.clientId)
                    .map((item) => ({
                        value: item.id,
                        label: `${item.firstName} ${item.middleName || ''} ${item.lastName}`.trim(),
                    }));
            } catch (error) {
                console.error(error);
                return [{value: null, label: ''}];
            }
        }, [users]);

        const onBound = async () => {
            if (!participant.id || !selectedUser?.value) return;
            try {
                await boundUserWithParticipantFx({
                    clientId: participant.id,
                    userId: selectedUser.value,
                });
                setSelectedUser(null);
                setAddUsersShow(false);
                await loadParticipantsDataFx();
            } catch (e) {
                toast.error(t('errors.occurred'));
            }
        };

        return (
            <>
                <div style={{minHeight: 160, maxHeight: 600, overflow: 'auto'}}>
                    <Table
                        columns={columns}
                        data={mappedData}
                        hideSetting={true}
                        emptyRowsLimit={{rows: 3, translateY: '20%'}}
                    />
                </div>
                <ButtonContainer style={{marginTop: '20px'}}>
                    <Button onClick={() => setAddUsersShow(true)} type="button">
                        + {t('pages.profile.staff.create.create')}
                    </Button>
                </ButtonContainer>
                <Modal
                    show={addUsersShow}
                    onClose={() => setAddUsersShow(false)}
                    title={t('pages.profile.participant.add.title')}
                    modalWidth={0}
                >
                    <div style={{paddingBottom: '6px'}}>
                        <Select
                            menuPlacement="top"
                            value={selectedUser}
                            options={userOptions}
                            placeholder={t('pages.profile.participant.fields.userIds')}
                            onChange={setSelectedUser}
                        />
                    </div>
                    <AddFooter>
                        <Button
                            variant={ButtonVariant.Secondary}
                            onClick={() => {
                                setAddUsersShow(false);
                                setSelectedUser(null);
                            }}
                        >
                            {t('pages.profile.participant.add.cancel')}
                        </Button>
                        <Button onClick={onBound}>{t('pages.profile.participant.add.ok')}</Button>
                    </AddFooter>
                </Modal>
            </>
        );
    };

    const tabsMapping: Record<number, JSX.Element> = {
        0: <StorageTab />,
        1: <PersonalTab />,
    };

    return (
        <form>
            <ParticipantTabs index={index} setIndex={setIndex} />
            {tabsMapping[index]}
        </form>
    );
};
